import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { DataGrid } from '@mui/x-data-grid'
import { Autocomplete, Box, Button, Checkbox, FormControl, FormHelperText, InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, Stack, Tab, Tabs, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment'
import useFetch from './api/api'
import CurrencyField from './components/CurrencyField'
import NumericField from './components/NumericField'
import config from './common/Config';

const MONTHS = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']

const NEW_BOND_OPTION = { label: '+ Criar novo título', bondId: null, isNew: true }

const emptyErrors = () => ({
	date:      '',
	operation: '',
	bondId:    '',
	quantity:  '',
	totalPaid: '',
})

const emptyBondErrors = () => ({
	name:      '',
	symbol:    '',
	coupon:    '',
	maturity:  '',
	payMonths: '',
})

const TransactionsFixedIncome = () => {

	const { market } = useParams();
	const privateFetch = useFetch();

	const [transactions, setTransactions] 	= useState([]);
	const [bonds, setBonds] 				= useState();

	const [date, setDate] 					= useState(0);
	const [inputFields, setInputFields] 	= useState([{
		assetType: ( market === 'BR' ? 'TESOURODIRETO' : 'BOND' ),
		operation: '',
		quantity: 0,
		totalPaid: 0,
		cost: 0,
		bondId: 0
	}])

	const [formErrors, setFormErrors] = useState(emptyErrors())

	// Estado para criação inline de bond (US only)
	const [isCreatingBond, setIsCreatingBond]     = useState(false)
	const [newBondName, setNewBondName]           = useState('UNITED STATES TREAS NTS')
	const [newBondSymbol, setNewBondSymbol]       = useState('')
	const [newBondCoupon, setNewBondCoupon]       = useState('')
	const [newBondMaturity, setNewBondMaturity]   = useState(null)
	const [newBondPayMonths, setNewBondPayMonths] = useState([])
	const [bondErrors, setBondErrors]             = useState(emptyBondErrors())

	const [currentTabIndex, setCurrentTabIndex] = useState(0);

	const addTabProps = (index) => ({
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	})

	const handleTabChange = (e, tabIndex) => {
		if (tabIndex === 1) {
			setIsCreatingBond(false)
			setBondErrors(emptyBondErrors())
		}
		setCurrentTabIndex(tabIndex);
	};

	useEffect(() => {
		if (currentTabIndex === 0) {
			privateFetch(config.apiUrl +"/api/transactions/"+ (market === 'BR' ? 'TESOURODIRETO' : 'BOND'))
			.then((data) => data.json())
			.then((data) => setTransactions(data))
		}
		else {
			privateFetch(config.apiUrl +"/api/bonds/"+ market)
			.then((data) => data.json())
			.then((data) => setBonds(data))
		}
	}, [currentTabIndex, market])

	const handleFormChange = (index, event) => {
		const data = [...inputFields];
		data[index][event.target.name] = event.target.value;
		setInputFields(data);
	}

	const handleBondSelect = (event, values) => {
		if (values?.isNew) {
			setIsCreatingBond(true)
			const data = [...inputFields]
			data[0].bondId = 0
			setInputFields(data)
		} else {
			setIsCreatingBond(false)
			const data = [...inputFields]
			data[0].bondId = values?.bondId ?? 0
			setInputFields(data)
		}
	}

	const isValid = () => {
		const next = emptyErrors()
		let valid = true

		if (!date || date === 0) {
			next.date = 'Informe a data'
			valid = false
		}
		if (!inputFields[0].operation) {
			next.operation = 'Selecione a operação'
			valid = false
		}
		if (!isCreatingBond && (!inputFields[0].bondId || inputFields[0].bondId === 0)) {
			next.bondId = 'Selecione o título'
			valid = false
		}
		if (!inputFields[0].quantity || Number(inputFields[0].quantity) === 0) {
			next.quantity = 'Informe a quantidade'
			valid = false
		}
		if (!inputFields[0].totalPaid || Number(inputFields[0].totalPaid) === 0) {
			next.totalPaid = 'Informe o valor total'
			valid = false
		}

		setFormErrors(next)
		return valid
	}

	const isBondValid = () => {
		const next = emptyBondErrors()
		let valid = true

		if (!newBondName || newBondName.trim().length === 0) {
			next.name = 'Informe o nome do título'
			valid = false
		}
		if (!newBondSymbol || newBondSymbol.trim().length === 0) {
			next.symbol = 'Informe o símbolo'
			valid = false
		}
		if (!newBondCoupon || isNaN(parseFloat(newBondCoupon))) {
			next.coupon = 'Informe o cupom (ex: 4,25)'
			valid = false
		}
		if (!newBondMaturity) {
			next.maturity = 'Informe a data de vencimento'
			valid = false
		}
		if (newBondPayMonths.length === 0) {
			next.payMonths = 'Selecione ao menos um mês de pagamento'
			valid = false
		}

		setBondErrors(next)
		return valid
	}

	const handleSaveTransactions = async () => {
		const txValid   = isValid()
		const bondValid = isCreatingBond ? isBondValid() : true
		if (!txValid || !bondValid) return

		const data = [...inputFields]
		data[0].quantity  = data[0].operation === 'SELL' ? Math.abs(data[0].quantity) * -1 : Math.abs(data[0].quantity)
		data[0].totalPaid = data[0].operation === 'SELL' ? Math.abs(data[0].totalPaid) * -1 : Math.abs(data[0].totalPaid)
		data[0].cost      = data[0].operation === 'SELL' ? Math.abs(data[0].cost) * -1 : Math.abs(data[0].cost)

		if (isCreatingBond) {
			const bondRes = await privateFetch(config.apiUrl + '/api/bonds', {
				method: 'POST',
				headers: { 'content-type': 'application/json; charset=utf-8' },
				body: JSON.stringify({
					name:      newBondName,
					symbol:    newBondSymbol,
					country:   'US',
					coupon:    newBondCoupon,
					maturity:  newBondMaturity,
					payMonths: newBondPayMonths,
				})
			})
			if (bondRes.status !== 200) return
			const createdBond = await bondRes.json()
			data[0].bondId = createdBond.id
		}

		setInputFields(data)

		await privateFetch(config.apiUrl + '/api/transactions', {
			method: 'POST',
			headers: { 'content-type': 'application/json; charset=utf-8' },
			body: JSON.stringify({ date, transactions: data })
		})
		.then(res => res.json())
		.then(() => setCurrentTabIndex(0))
	}

	const BRLReais = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

	const columns = [
		{
			field: 'date',
			headerName: 'Data',
			renderCell: (cellValues) => moment(cellValues.row.date).format("DD/MM/YYYY")
		},
		{ field: 'stockTicker', headerName: 'Ticker' },
		{ field: 'operation',   headerName: 'Operação' },
		{ field: 'quantity',    headerName: 'Qtd' },
		{ field: 'total',       headerName: 'Valor' },
		{ field: 'cost',        headerName: 'Custo' },
		{
			field: 'totalPaid',
			headerName: 'Total',
			renderCell: (cellValues) => BRLReais.format(parseFloat(cellValues.row.total) + parseFloat(cellValues.row.cost))
		},
	]

	return (
		<Paper sx={{ marginLeft: 2, marginRight: 4, marginBottom: 2 }} elevation={2}>
			<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
				<Tabs value={currentTabIndex} onChange={handleTabChange}>
					<Tab label="Transações" {...addTabProps(0)} />
					<Tab label="Adicionar Nova" {...addTabProps(1)} />
				</Tabs>
			</Box>

			{/* TAB 1 - LISTAGEM */}
			{currentTabIndex === 0 && (
				<DataGrid
					rows={transactions}
					getRowId={(row) => row.id}
					columns={columns}
					pageSize={12}
					rowHeight={30}
					autoHeight={true}
					sx={{
						fontSize: 12,
						width: '100%',
						'& .super-app.negative': { color: '#008000', fontWeight: '600' },
						'& .super-app.positive': { color: '#FF0000', fontWeight: '600' },
					}}
				/>
			)}

			{/* TAB 2 - ADICIONAR NOVA - BR */}
			{currentTabIndex === 1 && market === 'BR' && (
				<Box>
					<Stack spacing={2} padding={2} direction="row" alignItems="flex-start">
						<Autocomplete
							options={bonds?.map(b => ({ label: b.name, bondId: b.id })) ?? []}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Título"
									error={!!formErrors.bondId}
									helperText={formErrors.bondId}
								/>
							)}
							sx={{ width: 300 }}
							onChange={handleBondSelect}
						/>
						<DatePicker
							format="DD/MM/YYYY"
							label="Data"
							slotProps={{
								textField: {
									sx: { minWidth: 200 },
									error: !!formErrors.date,
									helperText: formErrors.date,
								}
							}}
							onChange={value => setDate(value)}
						/>
						<FormControl sx={{ minWidth: 120 }} error={!!formErrors.operation}>
							<InputLabel>Operação</InputLabel>
							<Select
								name="operation"
								value={inputFields[0].operation}
								label="Operação"
								onChange={event => handleFormChange(0, event)}
							>
								<MenuItem value="BUY">Compra</MenuItem>
								<MenuItem value="SELL">Venda</MenuItem>
							</Select>
							{formErrors.operation && <FormHelperText>{formErrors.operation}</FormHelperText>}
						</FormControl>
						<TextField
							name="quantity"
							label="Quantidade"
							sx={{ width: 160 }}
							error={!!formErrors.quantity}
							helperText={formErrors.quantity}
							onBlur={event => handleFormChange(0, event)}
						/>
						<CurrencyField
							name="totalPaid"
							label="Total"
							sx={{ width: 180 }}
							error={!!formErrors.totalPaid}
							helperText={formErrors.totalPaid}
							onChange={event => handleFormChange(0, event)}
						/>
						<CurrencyField
							name="cost"
							label="Custo"
							sx={{ width: 160 }}
							onChange={event => handleFormChange(0, event)}
						/>
					</Stack>
					<Stack padding={2} paddingTop={0}>
						<Box><Button variant="contained" onClick={handleSaveTransactions}>Salvar</Button></Box>
					</Stack>
				</Box>
			)}

			{/* TAB 2 - ADICIONAR NOVA - US */}
			{currentTabIndex === 1 && market === 'US' && (
				<Box>
					<Stack spacing={2} padding={2} direction="column">

						{/* Linha 1: Título + campos de novo título (se criar novo) ou campos de transação */}
						<Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
							<Autocomplete
								options={[
									NEW_BOND_OPTION,
									...(bonds?.map(b => ({ label: b.symbol, bondId: b.id })) ?? []),
								]}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Título"
										error={!!formErrors.bondId}
										helperText={formErrors.bondId}
									/>
								)}
								sx={{ width: 260 }}
								onChange={handleBondSelect}
							/>

							{isCreatingBond ? (
								<>
									<TextField
										label="Nome"
										sx={{ width: 240 }}
										value={newBondName}
										error={!!bondErrors.name}
										helperText={bondErrors.name}
										onChange={e => setNewBondName(e.target.value)}
									/>
									<TextField
										label="Symbol"
										sx={{ width: 120 }}
										value={newBondSymbol}
										error={!!bondErrors.symbol}
										helperText={bondErrors.symbol}
										onChange={e => setNewBondSymbol(e.target.value)}
									/>
									<NumericField
										label="Cupom"
										adornment="%"
										sx={{ width: 130 }}
										value={newBondCoupon}
										error={!!bondErrors.coupon}
										helperText={bondErrors.coupon}
										onChange={e => setNewBondCoupon(e.target.value)}
									/>
									<DatePicker
										format="DD/MM/YYYY"
										label="Vencimento"
										slotProps={{
											textField: {
												sx: { width: 170 },
												error: !!bondErrors.maturity,
												helperText: bondErrors.maturity,
											}
										}}
										onChange={value => setNewBondMaturity(value ? new Date(value).toISOString().substring(0, 10) : null)}
									/>
									<FormControl sx={{ width: 210 }} error={!!bondErrors.payMonths}>
										<InputLabel>Pagamentos</InputLabel>
										<Select
											multiple
											input={<OutlinedInput label="Pagamentos" />}
											value={newBondPayMonths}
											renderValue={selected => selected.join(', ')}
											onChange={e => setNewBondPayMonths(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
										>
											{MONTHS.map(month => (
												<MenuItem key={month} value={month}>
													<Checkbox checked={newBondPayMonths.indexOf(month) > -1} />
													<ListItemText primary={month} />
												</MenuItem>
											))}
										</Select>
										{bondErrors.payMonths && <FormHelperText>{bondErrors.payMonths}</FormHelperText>}
									</FormControl>
								</>
							) : (
								<>
									<DatePicker
										format="DD/MM/YYYY"
										label="Data"
										slotProps={{
											textField: {
												sx: { width: 190 },
												error: !!formErrors.date,
												helperText: formErrors.date,
											}
										}}
										onChange={value => setDate(value)}
									/>
									<FormControl sx={{ minWidth: 120 }} error={!!formErrors.operation}>
										<InputLabel>Operação</InputLabel>
										<Select
											name="operation"
											value={inputFields[0].operation}
											label="Operação"
											onChange={event => handleFormChange(0, event)}
										>
											<MenuItem value="BUY">Compra</MenuItem>
											<MenuItem value="SELL">Venda</MenuItem>
										</Select>
										{formErrors.operation && <FormHelperText>{formErrors.operation}</FormHelperText>}
									</FormControl>
									<TextField
										name="quantity"
										label="Quantidade"
										sx={{ width: 150 }}
										error={!!formErrors.quantity}
										helperText={formErrors.quantity}
										onBlur={event => handleFormChange(0, event)}
									/>
									<CurrencyField
										name="totalPaid"
										label="Total"
										sx={{ width: 170 }}
										error={!!formErrors.totalPaid}
										helperText={formErrors.totalPaid}
										onChange={event => handleFormChange(0, event)}
									/>
									<CurrencyField
										name="cost"
										label="Custo"
										sx={{ width: 150 }}
										onChange={event => handleFormChange(0, event)}
									/>
								</>
							)}
						</Stack>

						{/* Linha 2: campos de transação (apenas quando criar novo está ativo) */}
						{isCreatingBond && (
							<Stack direction="row" spacing={2} alignItems="flex-start">
								<DatePicker
									format="DD/MM/YYYY"
									label="Data"
									slotProps={{
										textField: {
											sx: { width: 190 },
											error: !!formErrors.date,
											helperText: formErrors.date,
										}
									}}
									onChange={value => setDate(value)}
								/>
								<FormControl sx={{ minWidth: 120 }} error={!!formErrors.operation}>
									<InputLabel>Operação</InputLabel>
									<Select
										name="operation"
										value={inputFields[0].operation}
										label="Operação"
										onChange={event => handleFormChange(0, event)}
									>
										<MenuItem value="BUY">Compra</MenuItem>
										<MenuItem value="SELL">Venda</MenuItem>
									</Select>
									{formErrors.operation && <FormHelperText>{formErrors.operation}</FormHelperText>}
								</FormControl>
								<TextField
									name="quantity"
									label="Quantidade"
									sx={{ width: 150 }}
									error={!!formErrors.quantity}
									helperText={formErrors.quantity}
									onBlur={event => handleFormChange(0, event)}
								/>
								<CurrencyField
									name="totalPaid"
									label="Total"
									sx={{ width: 170 }}
									error={!!formErrors.totalPaid}
									helperText={formErrors.totalPaid}
									onChange={event => handleFormChange(0, event)}
								/>
								<CurrencyField
									name="cost"
									label="Custo"
									sx={{ width: 150 }}
									onChange={event => handleFormChange(0, event)}
								/>
							</Stack>
						)}

					</Stack>
					<Stack padding={2} paddingTop={0}>
						<Box><Button variant="contained" onClick={handleSaveTransactions}>Salvar</Button></Box>
					</Stack>
				</Box>
			)}
		</Paper>
	);
}

export default TransactionsFixedIncome
